const SESSION_EXPIRED_DETAIL = 'session_expired';
const SESSION_EXPIRED_PARAM = 'sessionExpired';

export interface SessionExpiredHandlerOptions {
  logout?: () => void;
}

let registeredLogoutHandler: (() => void) | undefined;
let sessionExpirationRedirectInProgress = false;

export const registerSessionExpirationLogout = (logout?: () => void) => {
  registeredLogoutHandler = logout;
};

const getRegisteredLogout = (logout?: () => void) => logout ?? registeredLogoutHandler;

const buildLoginRedirectUrl = (): string => {
  if (typeof window === 'undefined') {
    return `/login?${SESSION_EXPIRED_PARAM}=1`;
  }

  try {
    const url = new URL('/login', window.location.origin);
    url.searchParams.set(SESSION_EXPIRED_PARAM, '1');
    return url.toString();
  } catch (error) {
    return `/login?${SESSION_EXPIRED_PARAM}=1`;
  }
};

export const isSessionExpiredDetail = (detail: unknown): boolean => {
  return typeof detail === 'string' && detail === SESSION_EXPIRED_DETAIL;
};

export const shouldHandleSessionExpiration = (
  status?: number,
  detail?: unknown,
): boolean => {
  return status === 401 && isSessionExpiredDetail(detail);
};

const handleSessionExpirationSideEffects = (logout?: () => void) => {
  try {
    logout?.();
  } catch (error) {
    console.error('Failed to logout during session expiration handling', error);
  }

  redirectToLoginForSessionExpiration();
};

export const redirectToLoginForSessionExpiration = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (sessionExpirationRedirectInProgress) {
    return;
  }

  sessionExpirationRedirectInProgress = true;

  const targetUrl = buildLoginRedirectUrl();

  if (window.location.href === targetUrl) {
    window.location.reload();
    return;
  }

  window.location.href = targetUrl;
};

export const handleSessionExpiredResponse = async (
  response: Response,
  options?: SessionExpiredHandlerOptions,
): Promise<boolean> => {
  const data = await response
    .clone()
    .json()
    .catch(() => null) as { detail?: unknown } | null;

  if (!shouldHandleSessionExpiration(response.status, data?.detail)) {
    return false;
  }

  handleSessionExpirationSideEffects(getRegisteredLogout(options?.logout));
  return true;
};
